import { AxiosPromise } from 'axios';

import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import { queryTypes, serviceTaskTypes } from 'src/types';
import { fileHelper, urlHelper } from '../../helpers';
import { serviceLocals } from 'src/locals';

export const lookupTaskTable = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceTaskTypes.TaskLookupDataType): AxiosPromise<serviceTaskTypes.TaskTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    return axios.get(urlHelper.addQueryParameters({ search: value }, urlsConfig.task.lookupTask), {
      blockSpinner,
    });
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.task.lookupTask), {
    blockSpinner,
  });
};

export const lookupTaskWithEvent = ({
  limit,
  offset,
  ids,
  value,
  order,
  eventIds,
  sortOrder,
  blockSpinner,
  load,
}: serviceTaskTypes.TaskLookupDataType): AxiosPromise<serviceTaskTypes.TaskTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  if (eventIds) {
    return axios.get(`${urlsConfig.task.lookupTask}?eventIds=${eventIds}`, {
      blockErrorHandler: true,
    });
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.task.lookupTask), {
    blockSpinner,
  });
};

export const lookupTaskOfOntology = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceTaskTypes.TaskLookupDataType): AxiosPromise<serviceTaskTypes.TaskOfOntologyLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.task.lookupTaskOfOntology), {
    blockSpinner,
  });
};

export const lookupTaskOfCCR = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceTaskTypes.TaskLookupDataType): AxiosPromise<serviceTaskTypes.TaskOfCCRLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.task.lookupTaskOfCCR), {
    blockSpinner,
  });
};

export const lookupTaskOfTT = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceTaskTypes.TaskLookupDataType): AxiosPromise<serviceTaskTypes.TaskOfTTLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.task.lookupTaskOfTT), {
    blockSpinner,
  });
};

export const createTask = (
  form: serviceTaskTypes.TaskCreateDataType,
  // formData: FormData,
  options: { blockErrorHandler: boolean },
) => {
  // const trainingData = fileHelper.convertArrayData(form.secondaryTrainingObjective);
  // const entitiesData = fileHelper.convertArrayData(form.relatedAudienceEntities);
  // const procesesData = fileHelper.convertArrayData(form.relatedAudienceProcesses);

  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.workPackageNumberId) {
    formData.append('workPackageNumberId', form.workPackageNumberId);
  }

  if (form.taskNumber) {
    formData.append('taskNumber', form.taskNumber);
  }

  if (form.taskName) {
    formData.append('taskName', form.taskName);
  }
  if (form.units && form.units.length > 0) {
    formData.append('units', form.units.join(','));
  }

  if (form.primaryRequirementId) {
    formData.append('primaryRequirementId', form.primaryRequirementId);
  }

  if (form.primaryRequirementType) {
    formData.append('primaryRequirementType', form.primaryRequirementType);
  }

  if (form.ccrIds && form.ccrIds.length > 0) {
    formData.append('ccrIds', form.ccrIds.join(','));
  }

  if (form.ttIds && form.ttIds?.length > 0) {
    formData.append('ttIds', form.ttIds.join(','));
  }

  if (form.startDateTimeGroup) {
    formData.append('startDateTimeGroup', form.startDateTimeGroup.toString());
  }

  if (form.endDateTimeGroup) {
    formData.append('endDateTimeGroup', form.endDateTimeGroup.toString());
  }

  if (form.relatedAudienceEntities) {
    formData.append('relatedAudienceEntities', form.relatedAudienceEntities.join(','));
  }

  if (form.relatedAudienceProcesses) {
    formData.append('relatedAudienceProcesses', form.relatedAudienceProcesses.join(','));
  }

  if (form.scenarioConditions) {
    formData.append('scenarioConditions', form.scenarioConditions);
  }

  if (form.scenarioConditionStatus) {
    formData.append('scenarioConditionStatus', form.scenarioConditionStatus);
  }

  if (form.milConditions) {
    formData.append('milConditions', form.milConditions);
  }

  if (form.milConditionStatus) {
    formData.append('milConditionStatus', form.milConditionStatus);
  }

  if (form.description) {
    formData.append('description', form.description);
  }

  // if (form.log) {
  //   formData.append('log', form.log);
  // }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  return axios.post(urlsConfig.task.createTask, formData, { headers: serviceLocals.formDataHeader });
};

export const taskEdit = (
  form: serviceTaskTypes.TaskEditDataType,
  // formData: FormData,
): AxiosPromise<serviceTaskTypes.TaskEditResultType> => {
  // const trainingData = fileHelper.convertArrayData(form.secondaryTrainingObjective);
  const entitiesData = fileHelper.convertArrayData(form.relatedAudienceEntities);
  const procesesData = fileHelper.convertArrayData(form.relatedAudienceProcesses);
  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.id !== undefined) {
    formData.append('id', form.id);
  }

  if (form.deletedAttachments !== undefined) {
    form.deletedAttachments.forEach((eachAttachment: any) => {
      formData.append('deletedAttachments', eachAttachment);
    });
  }

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.workPackageNumberId) {
    formData.append('workPackageNumberId', form.workPackageNumberId);
  }

  if (form.taskNumber) {
    formData.append('taskNumber', form.taskNumber);
  }

  if (form.taskName) {
    formData.append('taskName', form.taskName);
  }
  if (form.units && form.units.length > 0) {
    formData.append('units', form.units.join(','));
  }

  if (form.primaryRequirementId) {
    formData.append('primaryRequirementId', form.primaryRequirementId);
  }

  if (form.primaryRequirementType) {
    formData.append('primaryRequirementType', form.primaryRequirementType);
  }

  if (form.ccrIds && form.ccrIds.length > 0) {
    formData.append('ccrIds', form.ccrIds.join(','));
  }

  if (form.ttIds && form.ttIds?.length > 0) {
    formData.append('ttIds', form.ttIds.join(','));
  }

  if (form.startDateTimeGroup) {
    formData.append('startDateTimeGroup', form.startDateTimeGroup.toString());
  }

  if (form.endDateTimeGroup) {
    formData.append('endDateTimeGroup', form.endDateTimeGroup.toString());
  }

  if (form.relatedAudienceEntities) {
    formData.append('relatedAudienceEntities', form.relatedAudienceEntities.join(','));
  }

  if (form.relatedAudienceProcesses) {
    formData.append('relatedAudienceProcesses', form.relatedAudienceProcesses.join(','));
  }

  if (form.scenarioConditions) {
    formData.append('scenarioConditions', form.scenarioConditions);
  }

  if (form.scenarioConditionStatus) {
    formData.append('scenarioConditionStatus', form.scenarioConditionStatus);
  }

  if (form.milConditions) {
    formData.append('milConditions', form.milConditions);
  }

  if (form.milConditionStatus) {
    formData.append('milConditionStatus', form.milConditionStatus);
  }

  if (form.description) {
    formData.append('description', form.description);
  } else {
    formData.append('description', '');
  }

  // if (form.log) {
  //   formData.append('log', form.log);
  // }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  return axios.patch(urlsConfig.task.taskEdit, formData, { blockErrorHandler: true });
};

export const taskDelete = ({
  id,
}: serviceTaskTypes.TaskDeleteDataType): AxiosPromise<serviceTaskTypes.TaskDeleteResultType> => {
  return axios.delete(`${urlsConfig.task.taskDelete}/${id}`);
};
